import React, { useState, useEffect, useMemo, memo, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  getOrdersFormError,
  getOrdersFormLoading,
  getOrdersFormData,
  getVisibleFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getOrdersEnums, getOrdersForm } from '../../../store/order/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { CardTabs } from './ui/tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import FormsHeader from './ui/forms/form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { MeasurementModal } from './ui/modals/modal-measurement'
import { ManufactureModal } from './ui/modals/modal-manufacture'
import { DeliveryModal } from './ui/modals/modal-delivery'
import { Result, Skeleton } from 'antd'
import { clearOrdersFormData } from '../../../store/order/actions'
import { OrderFormSaveProvider } from './order-form-save-context'

function DetailPage() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const entityType = 'order'
  const ordersFormIsLoading = useSelector(getOrdersFormLoading)
  const ordersFormError = useSelector(getOrdersFormError)
  const ordersFormData = useSelector(getOrdersFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult, createClaim } = useSaveResult()
  const [enumsRequested, setEnumsRequested] = useState(false)
  const [forceRenderKey, setForceRenderKey] = useState(0)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isStageTransitioning, setIsStageTransitioning] = useState(false)
  const stageTransitionLockRef = useRef(false)

  const pageTitle = useMemo(() => `Заказ №${id}`, [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/orders',
        title: 'Заказы',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const runStageTransition = useCallback(
    async (stageCode?: string, formOverride?: typeof visibleFormData) => {
      if (stageTransitionLockRef.current) {
        return
      }
      stageTransitionLockRef.current = true
      setIsStageTransitioning(true)
      try {
        await saveResult(
          id,
          formOverride || visibleFormData,
          ordersFormData?.stages?.current,
          false,
          () => {
            return
          },
          true,
          stageCode,
          ordersFormData?.stages,
        )
      } finally {
        stageTransitionLockRef.current = false
        setIsStageTransitioning(false)
      }
    },
    [id, saveResult, visibleFormData, ordersFormData],
  )

  const handleNextStageOnClick = useCallback(async () => {
    await runStageTransition(undefined)
  }, [runStageTransition])

  const handleToClaim = useCallback(async () => {
    await createClaim(id)
  }, [createClaim, id])

  const handleSetDone = useCallback(async () => {
    await runStageTransition('done')
  }, [runStageTransition])

  const handleCancel = useCallback(() => {
    setIsOpenModal(false)
  }, [setIsOpenModal])

  const handleOk = useCallback(
    async modalFormData => {
      if (!visibleFormData) {
        return
      }
      const updatedVisibleFormData = {
        ...visibleFormData,
        modalFieldsSchema: modalFormData,
        modalValid: true,
      }
      await runStageTransition(undefined, updatedVisibleFormData)
      setForceRenderKey(prev => prev + 1)
      setIsOpenModal(false)
    },
    [runStageTransition, visibleFormData],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setEnumsRequested(false)
      dispatch(getOrdersForm(id))
    }
    return () => {
      mounted = false
      dispatch(clearOrdersFormData())
    }
  }, [id, dispatch])

  useEffect(() => {
    if (ordersFormData && id && !ordersFormError && !ordersFormIsLoading && !enumsRequested) {
      setEnumsRequested(true)
      if (id) {
        dispatch(getOrdersEnums(id))
      }
    }
  }, [ordersFormData, ordersFormError, ordersFormIsLoading, enumsRequested, id, dispatch])

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {ordersFormIsLoading && <Skeleton active />}
      {!ordersFormIsLoading && ordersFormError && !ordersFormData && (
        <Result
          status={
            typeof ordersFormError === 'object' && Number(ordersFormError.statusCode) === 404
              ? 404
              : typeof ordersFormError === 'object'
              ? (ordersFormError.statusCode as any) || 'error'
              : 'error'
          }
          title={
            typeof ordersFormError === 'object' && Number(ordersFormError.statusCode) === 404
              ? 'Не найдено'
              : 'Ошибка'
          }
          subTitle={typeof ordersFormError === 'object' ? ordersFormError.message : ordersFormError}
        />
      )}
      {ordersFormData && (
        <>
          <DefaultJsonFormContext.Provider value={defaultProps}>
            <OrderFormSaveProvider>
              <FormsHeader
                id={id}
                stage={ordersFormData?.stages?.current}
                nextStageOnClick={handleNextStageOnClick}
                toClaim={handleToClaim}
                isDisabled={false}
                withoutMontage={false}
                setDone={handleSetDone}
                onOpenModal={() => setIsOpenModal(true)}
                isStageTransitioning={isStageTransitioning}
              />
              <CardTabs key={forceRenderKey} id={id} />
              {visibleFormData && visibleFormData?.modalName === 'measurement' && (
                <MeasurementModal
                  id={id}
                  isModalOpen={isOpenModal}
                  onCancel={handleCancel}
                  onOk={handleOk}
                />
              )}
              {visibleFormData && visibleFormData?.modalName === 'manufacture' && (
                <ManufactureModal
                  id={id}
                  isModalOpen={isOpenModal}
                  onCancel={handleCancel}
                  onOk={handleOk}
                />
              )}
              {visibleFormData && visibleFormData?.modalName === 'delivery' && (
                <DeliveryModal
                  id={id}
                  isModalOpen={isOpenModal}
                  onCancel={handleCancel}
                  onOk={handleOk}
                />
              )}
            </OrderFormSaveProvider>
          </DefaultJsonFormContext.Provider>
        </>
      )}
    </B2BLayout>
  )
}

export default memo(DetailPage)
